import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Save, AlertCircle, CheckCircle, Loader, Sparkles, CreditCard as Edit3, FileText, Cpu, Key, Image, ExternalLink, MessageSquare, Type } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { adminUpdate, adminConfigApi } from '../../lib/adminApi';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { SlideOver } from '../../components/admin/SlideOver';
import { TabNavigation } from '../../components/admin/TabNavigation';

interface AIConfig {
  id: string;
  prompt_type: string;
  provider: string;
  model_name: string;
  temperature: number;
  max_tokens: number;
  top_p: number;
  frequency_penalty: number;
  presence_penalty: number;
  notes: string;
  is_active: boolean;
}

interface PromptTemplate {
  id: string;
  prompt_type: string;
  prompt_content: string;
  version: number;
  is_active: boolean;
}

interface CombinedPromptConfig {
  prompt_type: string;
  config: AIConfig;
  prompt: PromptTemplate;
}

interface ChatbotConfig {
  id: string;
  system_prompt: string;
  updated_at: string;
}

const promptTypeLabels: Record<string, string> = {
  idea_generate: 'Idee Generatie',
  article_generate: 'Artikel Genereren',
  article_write: 'Artikel Schrijven',
  article_regenerate: 'Artikel Herschrijven',
  titles_generate: 'Titels Genereren',
  intros_generate: "Intro's Genereren",
  images_prompt_enhance: 'Afbeelding Prompt Verbetering',
  blog_to_email: 'Blog naar Email Conversie',
  text_grammar_fix: 'Tekstcorrectie',
  text_full_rewrite: 'Tekst Herschrijven',
  chatbot_query: 'Chatbot Antwoorden',
  profile_generator_business: 'Profiel - Bedrijfsinfo',
  profile_generator_image: 'Profiel - Afbeeldingsstijl',
  onboarding_followup_question: 'Onboarding Doorvraag',
  article_direction_hints: 'Invalshoeken Genereren',
};

const promptTypeDescriptions: Record<string, string> = {
  idea_generate: 'Genereert blog ideeën op basis van bedrijfsprofiel',
  article_generate: 'Schrijft een volledig artikel op basis van een goedgekeurd idee',
  article_write: 'Schrijft een volledig artikel op basis van een goedgekeurd idee',
  article_regenerate: 'Herschrijft een artikel met feedback van de gebruiker',
  titles_generate: 'Genereert 5 pakkende titels voor een artikel',
  intros_generate: "Genereert 5 verschillende intro's voor een artikel",
  images_prompt_enhance: 'Vertaalt artikel context naar gedetailleerde Leonardo AI prompts',
  blog_to_email: 'Converteert een blog naar een email campaign formaat',
  text_grammar_fix: 'Corrigeert grammatica en spelling in geïmporteerde tekst',
  text_full_rewrite: 'Herschrijft geïmporteerde tekst volledig in een nieuwe stijl',
  chatbot_query: 'Genereert antwoorden voor chatbot vragen op basis van kennisbank context',
  profile_generator_business: 'Analyseert gebruikersinvoer en genereert een gestructureerd bedrijfsprofiel tijdens onboarding',
  profile_generator_image: 'Analyseert gebruikersinvoer en genereert afbeeldingsstijl voorkeuren tijdens onboarding',
  onboarding_followup_question: 'Bepaalt of een onboarding antwoord een doorvraag verdient. Retourneert JSON: { needs_followup, question? }. Beschikbare placeholders: {question_label}, {user_answer}',
  article_direction_hints: 'Genereert mogelijke invalshoeken (angles) voor een goedgekeurd blogidee, zodat de gebruiker een richting kan kiezen voor het artikel',
};

export function AdminAISettings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'prompts' | 'openai' | 'leonardo' | 'bfl' | 'chatbot' | 'text-engine'>('prompts');
  const [combinedData, setCombinedData] = useState<CombinedPromptConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<'config' | 'prompt' | 'both' | 'chatbot' | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [chatbotConfig, setChatbotConfig] = useState<ChatbotConfig | null>(null);
  const [chatbotLoading, setChatbotLoading] = useState(false);
  const [chatbotAIConfig, setChatbotAIConfig] = useState<AIConfig | null>(null);

  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [leonardoApiKey, setLeonardoApiKey] = useState('');
  const [aiGenerationEnabled, setAiGenerationEnabled] = useState(false);

  const [leonardoModelId, setLeonardoModelId] = useState('');
  const [leonardoContrast, setLeonardoContrast] = useState('4.5');
  const [leonardoNumImages, setLeonardoNumImages] = useState('4');
  const [leonardoWidth, setLeonardoWidth] = useState('1472');
  const [leonardoHeight, setLeonardoHeight] = useState('832');
  const [leonardoUltra, setLeonardoUltra] = useState(false);
  const [leonardoStyleUuid, setLeonardoStyleUuid] = useState('');
  const [leonardoEnhancePrompt, setLeonardoEnhancePrompt] = useState(true);

  // Image engine multi-provider config (zie image-generate edge function)
  const [imageEngineDefault, setImageEngineDefault] = useState<'bfl' | 'leonardo' | 'nanobanana'>('bfl');
  const [imageEngineFallback, setImageEngineFallback] = useState<'bfl' | 'leonardo' | 'nanobanana' | 'none'>('leonardo');
  const [nanobananaResolution, setNanobananaResolution] = useState<'1k' | '2k'>('1k');

  // Text engine multi-provider config (zie openai-generate edge function)
  const [textEngineDefault, setTextEngineDefault] = useState<'openai' | 'anthropic'>('anthropic');
  const [textEngineFallback, setTextEngineFallback] = useState<'openai' | 'anthropic' | 'none'>('openai');
  const [textConfigsByPromptType, setTextConfigsByPromptType] = useState<Record<string, { openai?: Partial<AIConfig>; anthropic?: Partial<AIConfig> }>>({});
  const [textCostSummary, setTextCostSummary] = useState<{
    loading: boolean;
    range: '7d' | '30d';
    rows: Array<{ engine: string; calls: number; tokens: number; cost_eur: number; fallback_count: number }>;
  }>({ loading: false, range: '7d', rows: [] });
  type BflModelOption = 'flux-dev' | 'flux-pro-1.1-ultra' | 'flux-2-pro' | 'flux-2-flex' | 'flux-2-klein-9b';
  const BFL_MODEL_OPTIONS: BflModelOption[] = ['flux-dev', 'flux-pro-1.1-ultra', 'flux-2-pro', 'flux-2-flex', 'flux-2-klein-9b'];
  const [bflModel, setBflModel] = useState<BflModelOption>('flux-dev');
  const [bflUseRaw, setBflUseRaw] = useState(true);
  const [bflGuidance, setBflGuidance] = useState('2.5');
  const [bflSteps, setBflSteps] = useState('28');
  const [bflSafetyTolerance, setBflSafetyTolerance] = useState('5');
  const [bflCostPerImageUsd, setBflCostPerImageUsd] = useState('0.025');
  const [bflCostPerImageUltraUsd, setBflCostPerImageUltraUsd] = useState('0.06');
  const [usdEurRate, setUsdEurRate] = useState('0.92');

  const [systemConfigLoading, setSystemConfigLoading] = useState(false);

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CombinedPromptConfig | null>(null);
  const [editingConfig, setEditingConfig] = useState<AIConfig | null>(null);
  const [editingPromptContent, setEditingPromptContent] = useState('');
  const [originalConfig, setOriginalConfig] = useState<AIConfig | null>(null);
  const [originalPromptContent, setOriginalPromptContent] = useState('');
  const [panelMessage, setPanelMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadData();
    loadSystemConfig();
    loadChatbotConfig();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('No session found');
        setMessage({ type: 'error', text: 'Geen sessie gevonden' });
        setLoading(false);
        return;
      }

      const [configsResponse, promptsResponse] = await Promise.all([
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-data?type=ai-models`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        }),
        fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-get-data?type=prompt-templates`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        }),
      ]);

      if (!configsResponse.ok || !promptsResponse.ok) {
        throw new Error('Failed to fetch data');
      }

      const { data: configs } = await configsResponse.json();
      const { data: prompts } = await promptsResponse.json();

      const combined: CombinedPromptConfig[] = [];

      (configs || []).forEach((config: AIConfig) => {
        const matchingPrompt = (prompts || []).find(
          (p: PromptTemplate) => p.prompt_type === config.prompt_type && p.is_active
        );

        if (matchingPrompt) {
          combined.push({
            prompt_type: config.prompt_type,
            config,
            prompt: matchingPrompt,
          });
        }
      });

      setCombinedData(combined);
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({ type: 'error', text: 'Fout bij laden van AI configuraties' });
    } finally {
      setLoading(false);
    }
  };

  const loadSystemConfig = async () => {
    setSystemConfigLoading(true);
    try {
      const { data: configData } = await adminConfigApi.systemConfig();

      if (configData) {
        const configMap = configData.reduce((acc: any, item: any) => {
          acc[item.key] = item.value;
          return acc;
        }, {});

        setOpenaiApiKey(configMap['openai_api_key'] || '');
        setLeonardoApiKey(configMap['leonardo_api_key'] || '');
        setAiGenerationEnabled(configMap['ai_generation_enabled'] === 'true');

        setLeonardoModelId(configMap['leonardo_model_id'] || 'b2614463-296c-462a-9586-aafdb8f00e36');
        setLeonardoContrast(configMap['leonardo_contrast'] || '4.5');
        setLeonardoNumImages(configMap['leonardo_num_images'] || '4');
        setLeonardoWidth(configMap['leonardo_width'] || '1472');
        setLeonardoHeight(configMap['leonardo_height'] || '832');
        setLeonardoUltra(configMap['leonardo_ultra'] === 'true');
        setLeonardoStyleUuid(configMap['leonardo_style_uuid'] || '111dc692-d470-4eec-b791-3475abac4c46');
        setLeonardoEnhancePrompt(configMap['leonardo_enhance_prompt'] === 'true');

        const defaultEngine = configMap['image_engine_default'];
        setImageEngineDefault(
          defaultEngine === 'leonardo' ? 'leonardo' : defaultEngine === 'nanobanana' ? 'nanobanana' : 'bfl'
        );
        const fallbackEngine = configMap['image_engine_fallback'];
        setImageEngineFallback(
          fallbackEngine === 'bfl'
            ? 'bfl'
            : fallbackEngine === 'nanobanana'
              ? 'nanobanana'
              : fallbackEngine === 'none' || !fallbackEngine
                ? 'none'
                : 'leonardo'
        );
        setNanobananaResolution(configMap['nanobanana_resolution'] === '2k' ? '2k' : '1k');

        const textDefault = configMap['text_engine_default'];
        setTextEngineDefault(textDefault === 'openai' ? 'openai' : 'anthropic');
        const textFallback = configMap['text_engine_fallback'];
        setTextEngineFallback(
          textFallback === 'anthropic' ? 'anthropic' : textFallback === 'none' || !textFallback ? 'none' : 'openai'
        );
        setBflModel(
          BFL_MODEL_OPTIONS.includes(configMap['bfl_model'] as BflModelOption)
            ? (configMap['bfl_model'] as BflModelOption)
            : 'flux-dev'
        );
        setBflUseRaw(configMap['bfl_use_raw'] !== 'false');
        setBflGuidance(configMap['bfl_guidance'] || '2.5');
        setBflSteps(configMap['bfl_steps'] || '28');
        setBflSafetyTolerance(configMap['bfl_safety_tolerance'] || '5');
        setBflCostPerImageUsd(configMap['bfl_cost_per_image_usd'] || '0.025');
        setBflCostPerImageUltraUsd(configMap['bfl_cost_per_image_ultra_usd'] || '0.06');
        setUsdEurRate(configMap['usd_eur_rate'] || '0.92');
      }
    } catch (error) {
      console.error('Error loading system config:', error);
      setMessage({ type: 'error', text: 'Fout bij laden van systeem configuratie' });
    } finally {
      setSystemConfigLoading(false);
    }
  };

  const loadChatbotConfig = async () => {
    setChatbotLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Geen sessie gevonden');
      }

      // Load chatbot_config (system prompt)
      const { data: configData, error: configError } = await supabase
        .from('chatbot_config')
        .select('*')
        .single();

      if (configError) throw configError;

      if (configData) {
        setChatbotConfig(configData);
      }

      // Load ai_generation_configs for chatbot_query
      const { data: aiConfigData, error: aiConfigError } = await supabase
        .from('ai_generation_configs')
        .select('*')
        .eq('prompt_type', 'chatbot_query')
        .eq('is_active', true)
        .maybeSingle();

      if (aiConfigError) {
        console.error('Error loading AI config:', aiConfigError);
      }

      if (aiConfigData) {
        setChatbotAIConfig(aiConfigData);
      }
    } catch (error) {
      console.error('Error loading chatbot config:', error);
      setMessage({ type: 'error', text: 'Fout bij laden van chatbot configuratie' });
    } finally {
      setChatbotLoading(false);
    }
  };

  const saveSystemConfig = async (updates: Record<string, string>) => {
    setSaving('config');
    try {
      for (const [key, value] of Object.entries(updates)) {
        await adminUpdate({
          table: 'system_config',
          operation: 'update',
          filters: { key },
          data: { value },
        });
      }

      await loadSystemConfig();
      setMessage({ type: 'success', text: 'Configuratie succesvol opgeslagen' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving system config:', error);
      setMessage({ type: 'error', text: `Fout bij opslaan: ${error.message}` });
    } finally {
      setSaving(null);
    }
  };

  const handleSaveOpenAI = async () => {
    await saveSystemConfig({
      'openai_api_key': openaiApiKey,
      'ai_generation_enabled': aiGenerationEnabled ? 'true' : 'false',
    });
  };

  const handleSaveLeonardo = async () => {
    await saveSystemConfig({
      'leonardo_api_key': leonardoApiKey,
      'leonardo_model_id': leonardoModelId,
      'leonardo_contrast': leonardoContrast,
      'leonardo_num_images': leonardoNumImages,
      'leonardo_width': leonardoWidth,
      'leonardo_height': leonardoHeight,
      'leonardo_ultra': leonardoUltra ? 'true' : 'false',
      'leonardo_style_uuid': leonardoStyleUuid,
      'leonardo_enhance_prompt': leonardoEnhancePrompt ? 'true' : 'false',
    });
  };

  const handleSaveTextEngine = async () => {
    await saveSystemConfig({
      'text_engine_default': textEngineDefault,
      'text_engine_fallback': textEngineFallback,
    });
  };

  const loadTextEngineDetails = async () => {
    try {
      // Per prompt_type beide provider-configs ophalen voor read-only weergave.
      const { data: configs } = await supabase
        .from('ai_generation_configs')
        .select('prompt_type, provider, model_name, temperature, max_tokens, is_active')
        .eq('is_active', true)
        .in('provider', ['openai', 'anthropic']);

      const grouped: Record<string, { openai?: Partial<AIConfig>; anthropic?: Partial<AIConfig> }> = {};
      (configs ?? []).forEach((c: Partial<AIConfig> & { prompt_type: string; provider: string }) => {
        const slot = grouped[c.prompt_type] ?? {};
        if (c.provider === 'openai') slot.openai = c;
        if (c.provider === 'anthropic') slot.anthropic = c;
        grouped[c.prompt_type] = slot;
      });
      setTextConfigsByPromptType(grouped);
    } catch (e) {
      console.error('Failed to load text engine configs:', e);
    }
  };

  const loadTextCostSummary = async (range: '7d' | '30d') => {
    setTextCostSummary((s) => ({ ...s, loading: true, range }));
    try {
      const days = range === '7d' ? 7 : 30;
      const since = new Date();
      since.setDate(since.getDate() - days);

      const { data, error } = await supabase
        .from('ai_prompt_logs')
        .select('engine_used, fallback_triggered, response_metadata, success, provider')
        .gte('created_at', since.toISOString())
        .neq('prompt_type', 'images_generate')
        .eq('success', true);

      if (error) throw error;

      const agg: Record<string, { calls: number; tokens: number; cost_eur: number; fallback_count: number }> = {};
      (data ?? []).forEach((row: { engine_used: string | null; provider: string | null; fallback_triggered: boolean | null; response_metadata: Record<string, unknown> | null }) => {
        const engine = row.engine_used || row.provider || 'unknown';
        const rm = row.response_metadata ?? {};
        const tokens = Number(rm.tokens_used ?? 0);
        const costEur = Number(rm.cost_total_euro ?? 0);
        const bucket = agg[engine] ?? { calls: 0, tokens: 0, cost_eur: 0, fallback_count: 0 };
        bucket.calls += 1;
        bucket.tokens += tokens;
        bucket.cost_eur += costEur;
        if (row.fallback_triggered) bucket.fallback_count += 1;
        agg[engine] = bucket;
      });

      const rows = Object.entries(agg).map(([engine, v]) => ({
        engine,
        calls: v.calls,
        tokens: v.tokens,
        cost_eur: Number(v.cost_eur.toFixed(2)),
        fallback_count: v.fallback_count,
      }));

      setTextCostSummary({ loading: false, range, rows });
    } catch (e) {
      console.error('Failed to load text cost summary:', e);
      setTextCostSummary({ loading: false, range, rows: [] });
    }
  };

  useEffect(() => {
    if (activeTab === 'text-engine') {
      loadTextEngineDetails();
      loadTextCostSummary(textCostSummary.range);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleSaveBFL = async () => {
    await saveSystemConfig({
      'image_engine_default': imageEngineDefault,
      'image_engine_fallback': imageEngineFallback,
      'nanobanana_resolution': nanobananaResolution,
      'bfl_model': bflModel,
      'bfl_use_raw': bflUseRaw ? 'true' : 'false',
      'bfl_guidance': bflGuidance,
      'bfl_steps': bflSteps,
      'bfl_safety_tolerance': bflSafetyTolerance,
      'bfl_cost_per_image_usd': bflCostPerImageUsd,
      'bfl_cost_per_image_ultra_usd': bflCostPerImageUltraUsd,
      'usd_eur_rate': usdEurRate,
    });
  };

  const handleSaveChatbotConfig = async () => {
    if (!chatbotConfig) return;

    setSaving('chatbot');
    try {
      await adminUpdate({
        table: 'chatbot_config',
        operation: 'update',
        id: chatbotConfig.id,
        data: {
          system_prompt: chatbotConfig.system_prompt,
        },
      });

      await loadChatbotConfig();
      setMessage({ type: 'success', text: 'System prompt succesvol opgeslagen' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving chatbot config:', error);
      setMessage({ type: 'error', text: `Fout bij opslaan: ${error.message}` });
    } finally {
      setSaving(null);
    }
  };

  const handleSaveChatbotAIConfig = async () => {
    if (!chatbotAIConfig) return;

    setSaving('config');
    try {
      await adminUpdate({
        table: 'ai_generation_configs',
        operation: 'update',
        id: chatbotAIConfig.id,
        data: {
          model_name: chatbotAIConfig.model_name,
          temperature: chatbotAIConfig.temperature,
          max_tokens: chatbotAIConfig.max_tokens,
          top_p: chatbotAIConfig.top_p,
          frequency_penalty: chatbotAIConfig.frequency_penalty,
          presence_penalty: chatbotAIConfig.presence_penalty,
          notes: chatbotAIConfig.notes,
        },
      });

      await loadChatbotConfig();
      setMessage({ type: 'success', text: 'AI model instellingen succesvol opgeslagen' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving chatbot AI config:', error);
      setMessage({ type: 'error', text: `Fout bij opslaan: ${error.message}` });
    } finally {
      setSaving(null);
    }
  };

  const openPanel = (item: CombinedPromptConfig) => {
    setEditingItem(item);
    setEditingConfig({ ...item.config });
    setEditingPromptContent(item.prompt.prompt_content);
    setOriginalConfig({ ...item.config });
    setOriginalPromptContent(item.prompt.prompt_content);
    setIsPanelOpen(true);
    setPanelMessage(null);
  };

  const handleConfigChange = (field: keyof AIConfig, value: any) => {
    if (!editingConfig) return;
    setEditingConfig(prev => prev ? { ...prev, [field]: value } : null);
  };

  const closePanel = () => {
    const configChanged = JSON.stringify(editingConfig) !== JSON.stringify(originalConfig);
    const promptChanged = editingPromptContent !== originalPromptContent;
    const hasUnsavedChanges = configChanged || promptChanged;

    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'Je hebt niet-opgeslagen wijzigingen. Weet je zeker dat je wilt sluiten?'
      );
      if (!confirmed) return;
    }

    setIsPanelOpen(false);
    setEditingItem(null);
    setEditingConfig(null);
    setEditingPromptContent('');
    setOriginalConfig(null);
    setOriginalPromptContent('');
    setPanelMessage(null);
  };

  const handleSave = async () => {
    if (!editingItem || !editingConfig) return;

    setSaving('both');
    setPanelMessage(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Geen actieve sessie. Log opnieuw in.');
      }

      const configChanged = JSON.stringify(editingConfig) !== JSON.stringify(originalConfig);
      const promptChanged = editingPromptContent !== originalPromptContent;

      if (configChanged) {
        await adminUpdate({
          table: 'ai_generation_configs',
          operation: 'update',
          id: editingConfig.id,
          data: {
            model_name: editingConfig.model_name,
            temperature: editingConfig.temperature,
            max_tokens: editingConfig.max_tokens,
            top_p: editingConfig.top_p,
            frequency_penalty: editingConfig.frequency_penalty,
            presence_penalty: editingConfig.presence_penalty,
            notes: editingConfig.notes,
          },
        });
      }

      if (promptChanged) {
        await adminUpdate({
          table: 'prompt_templates',
          operation: 'update',
          id: editingItem.prompt.id,
          data: { prompt_content: editingPromptContent },
        });
      }

      await loadData();
      setOriginalConfig(editingConfig);
      setOriginalPromptContent(editingPromptContent);

      setPanelMessage({ type: 'success', text: 'Wijzigingen succesvol opgeslagen' });
      setTimeout(() => setPanelMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving:', error);
      setPanelMessage({
        type: 'error',
        text: `Fout bij opslaan: ${error.message}`
      });
    } finally {
      setSaving(null);
    }
  };

  const getPromptPlaceholders = (promptType: string) => {
    const placeholders: Record<string, string[]> = {
      idea_generate: ['{user_profile}', '{website_url}', '{previous_topics}', '{recent_published_blogs}'],
      article_write: ['{user_profile}', '{website_url}', '{idea_title}', '{idea_content}'],
      article_regenerate: ['{user_profile}', '{website_url}', '{article_content}', '{change_request}'],
      titles_generate: ['{user_profile}', '{website_url}', '{article_content}'],
      intros_generate: ['{user_profile}', '{website_url}', '{article_content}', '{selected_title}'],
      images_prompt_enhance: ['{idea_title}', '{article_content_summary}', '{article_body}', '{image_preferences}'],
      blog_to_email: ['{blog_title}', '{blog_intro}', '{blog_content}', '{blog_url}', '{accent_color}', '{greeting}', '{closing}'],
      text_grammar_fix: ['{{company_name}}', '{{website_url}}', '{{original_text}}'],
      text_full_rewrite: ['{{company_name}}', '{{website_url}}', '{{original_text}}'],
      profile_generator_business: ['{business_name}', '{what_they_do}', '{target_audience}', '{problem}', '{solution}', '{core_message}', '{tone}', '{website_url}'],
      profile_generator_image: ['{business_name}', '{image_style}', '{image_elements_include}', '{image_elements_exclude}', '{image_color_preferences}', '{tone}'],
      article_direction_hints: ['{user_profile}', '{idea_title}', '{idea_content}'],
    };

    return placeholders[promptType] || [];
  };

  const getPromptDescription = (promptType: string) => {
    return promptTypeDescriptions[promptType] || 'Geen beschrijving beschikbaar.';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Laden...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  const tabs = [
    { id: 'prompts' as const, label: 'AI Prompts', icon: Sparkles },
    { id: 'text-engine' as const, label: 'Tekst-engine', icon: Type },
    { id: 'openai' as const, label: 'OpenAI', icon: Key },
    { id: 'bfl' as const, label: 'BFL (Flux)', icon: Cpu },
    { id: 'leonardo' as const, label: 'Leonardo AI', icon: Image },
    { id: 'chatbot' as const, label: 'Chatbot AI', icon: MessageSquare },
  ];

  return (
    <AdminLayout>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Settings className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">AI Instellingen</h1>
        </div>
        <p className="text-gray-600">
          Configureer OpenAI, BFL (Flux), Leonardo AI en AI prompt templates
        </p>
      </div>

      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId as typeof activeTab)}
      />

      {message && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {activeTab === 'prompts' && (
        <>
          {combinedData.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Geen AI configuraties gevonden</p>
          <p className="text-sm text-gray-500">Controleer of de database migratie correct is uitgevoerd</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {combinedData.map((item) => {
            const preview = item.prompt.prompt_content.split('\n')
              .filter(line => line.trim().length > 0)
              .slice(0, 2)
              .join(' ')
              .substring(0, 100);

            return (
              <button
                key={item.prompt_type}
                onClick={() => openPanel(item)}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md hover:border-blue-300 transition-all text-left group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-blue-600" />
                    <span className="text-xs font-medium text-gray-500">
                      v{item.prompt.version}
                    </span>
                  </div>
                  <Edit3 className="w-4 h-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                </div>

                <h3 className="font-semibold text-gray-900 mb-2 leading-tight">
                  {promptTypeLabels[item.prompt_type] || item.prompt_type}
                </h3>

                <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                  {promptTypeDescriptions[item.prompt_type]}
                </p>

                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Cpu className="w-3 h-3 text-gray-400" />
                    <span className="text-xs text-gray-600 font-medium">
                      {item.config.model_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>Temp: {item.config.temperature}</span>
                    <span>•</span>
                    <span>Max: {item.config.max_tokens}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${item.config.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                    <span className="text-xs text-gray-500">
                      {item.config.is_active ? 'Actief' : 'Inactief'}
                    </span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 line-clamp-2 font-mono">
                    {preview}...
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
        </>
      )}

      {activeTab === 'text-engine' && (
        <div className="max-w-5xl space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Type className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Tekst-engine keuze</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Welk model wordt eerst gebruikt voor blog-, idee- en tekst-generatie. De fallback springt in
              als de primary faalt of een timeout geeft — de gebruiker merkt daar niets van.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primaire engine</label>
                <select
                  value={textEngineDefault}
                  onChange={(e) => setTextEngineDefault(e.target.value as 'openai' | 'anthropic')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="anthropic">Anthropic (Claude)</option>
                  <option value="openai">OpenAI (GPT)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Wordt eerst aangeroepen voor elke tekst-generatie.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fallback engine</label>
                <select
                  value={textEngineFallback}
                  onChange={(e) => setTextEngineFallback(e.target.value as 'openai' | 'anthropic' | 'none')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="openai">OpenAI (GPT)</option>
                  <option value="anthropic">Anthropic (Claude)</option>
                  <option value="none">Geen (faal direct)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Springt in als primary faalt. "Geen" = harde fout naar gebruiker.
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
              <button
                onClick={handleSaveTextEngine}
                disabled={saving === 'config'}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving === 'config' ? <><Loader className="w-4 h-4 animate-spin" />Opslaan...</> : <><Save className="w-4 h-4" />Opslaan</>}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Per prompt-type — actieve modellen</h3>
              <span className="text-xs text-gray-500">Read-only — bewerken via tab "AI Prompts"</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Prompt type</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">OpenAI</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Anthropic</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(textConfigsByPromptType).length === 0 ? (
                    <tr><td colSpan={3} className="py-6 text-center text-gray-500">Geen configuraties gevonden.</td></tr>
                  ) : (
                    Object.entries(textConfigsByPromptType)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([promptType, slot]) => (
                        <tr key={promptType} className="border-b border-gray-100">
                          <td className="py-2 px-3 font-medium text-gray-900">
                            {promptTypeLabels[promptType] || promptType}
                          </td>
                          <td className="py-2 px-3 text-gray-700">
                            {slot.openai ? (
                              <span><code className="text-xs">{slot.openai.model_name}</code> · t={slot.openai.temperature} · max={slot.openai.max_tokens}</span>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                          <td className="py-2 px-3 text-gray-700">
                            {slot.anthropic ? (
                              <span><code className="text-xs">{slot.anthropic.model_name}</code> · t={slot.anthropic.temperature} · max={slot.anthropic.max_tokens}</span>
                            ) : <span className="text-gray-400">—</span>}
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Kosten per engine</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => loadTextCostSummary('7d')}
                  className={`px-3 py-1.5 text-sm rounded-lg border ${textCostSummary.range === '7d' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  Laatste 7 dagen
                </button>
                <button
                  onClick={() => loadTextCostSummary('30d')}
                  className={`px-3 py-1.5 text-sm rounded-lg border ${textCostSummary.range === '30d' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                >
                  Laatste 30 dagen
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-700">Engine</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-700">Aantal calls</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-700">Tokens</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-700">Kosten (€)</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-700">Fallback triggers</th>
                  </tr>
                </thead>
                <tbody>
                  {textCostSummary.loading ? (
                    <tr><td colSpan={5} className="py-6 text-center text-gray-500"><Loader className="w-5 h-5 animate-spin inline" /></td></tr>
                  ) : textCostSummary.rows.length === 0 ? (
                    <tr><td colSpan={5} className="py-6 text-center text-gray-500">Nog geen calls in dit tijdvak.</td></tr>
                  ) : (
                    textCostSummary.rows.map((r) => (
                      <tr key={r.engine} className="border-b border-gray-100">
                        <td className="py-2 px-3 font-medium text-gray-900">{r.engine}</td>
                        <td className="py-2 px-3 text-right text-gray-700">{r.calls}</td>
                        <td className="py-2 px-3 text-right text-gray-700">{r.tokens.toLocaleString('nl-NL')}</td>
                        <td className="py-2 px-3 text-right text-gray-700">€{r.cost_eur.toFixed(2)}</td>
                        <td className="py-2 px-3 text-right text-gray-700">{r.fallback_count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Kosten en tokens komen uit <code>ai_prompt_logs</code>. Een hoog aantal fallback-triggers
              wijst op problemen met de primaire engine.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'openai' && (
        <div className="max-w-4xl">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Key className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">OpenAI Configuratie</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  OpenAI API Key
                </label>
                <input
                  type="password"
                  value={openaiApiKey}
                  onChange={(e) => setOpenaiApiKey(e.target.value)}
                  placeholder="sk-..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Deze key wordt gebruikt voor alle OpenAI API calls (tekst generatie, prompts, etc.)
                </p>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">AI Generatie Ingeschakeld</h3>
                    <p className="text-sm text-gray-500">
                      Schakel alle AI generatie functies in of uit
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={aiGenerationEnabled}
                      onChange={(e) => setAiGenerationEnabled(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={handleSaveOpenAI}
                  disabled={saving === 'config'}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving === 'config' ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Opslaan...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Opslaan
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'bfl' && (
        <div className="max-w-4xl">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <Cpu className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">BFL (Black Forest Labs)</h2>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Primaire image-engine. De BFL API-key wordt beheerd als Supabase secret
              (<code className="text-xs bg-gray-100 px-1 py-0.5 rounded">BFL_API_KEY</code>),
              niet via deze pagina. Roteer hem via{' '}
              <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">supabase secrets set BFL_API_KEY=...</code>.
            </p>

            <div className="space-y-6">
              <div className="pb-4 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Image engine keuze</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primaire engine
                    </label>
                    <select
                      value={imageEngineDefault}
                      onChange={(e) => setImageEngineDefault(e.target.value as 'bfl' | 'leonardo' | 'nanobanana')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="nanobanana">NanoBanana (via Leonardo)</option>
                      <option value="bfl">BFL (Flux)</option>
                      <option value="leonardo">Leonardo AI</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Wordt eerst aangeroepen voor elke image-generatie.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fallback engine
                    </label>
                    <select
                      value={imageEngineFallback}
                      onChange={(e) => setImageEngineFallback(e.target.value as 'bfl' | 'leonardo' | 'nanobanana' | 'none')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="leonardo">Leonardo AI</option>
                      <option value="bfl">BFL (Flux)</option>
                      <option value="nanobanana">NanoBanana (via Leonardo)</option>
                      <option value="none">Geen (faal direct)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Wordt aangeroepen als primaire engine faalt. Gebruiker merkt niets.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      NanoBanana resolutie
                    </label>
                    <select
                      value={nanobananaResolution}
                      onChange={(e) => setNanobananaResolution(e.target.value as '1k' | '2k')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="1k">1K — 1376×768 (±€0,06/beeld)</option>
                      <option value="2k">2K — 2752×1536 (±€0,09/beeld)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Alleen van toepassing wanneer NanoBanana als primaire engine draait.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pb-4 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  BFL parameters
                  <a
                    href="https://docs.bfl.ai/quick_start/generating_images"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                    title="BFL API documentatie"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Model variant
                    </label>
                    <select
                      value={bflModel}
                      onChange={(e) => setBflModel(e.target.value as BflModelOption)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="flux-2-pro">Flux 2 Pro (aanbevolen voor generatie)</option>
                      <option value="flux-2-klein-9b">Flux 2 Klein 9B (goedkoopst, Flux 2)</option>
                      <option value="flux-2-flex">Flux 2 Flex (premium, Flux 2)</option>
                      <option value="flux-dev">Flux Dev (oud, goedkoop)</option>
                      <option value="flux-pro-1.1-ultra">Flux Pro 1.1 Ultra (oud, raw photo)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Flux 2 Pro is de aanbevolen default. Flux Dev: guidance/steps instelbaar. Ultra: raw-mode. Flux 2-modellen gebruiken geen guidance/steps/raw.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Guidance (1.5 – 5.0)
                    </label>
                    <input
                      type="number"
                      min="1.5"
                      max="5"
                      step="0.1"
                      value={bflGuidance}
                      onChange={(e) => setBflGuidance(e.target.value)}
                      disabled={bflModel !== 'flux-dev'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                    />
                    <p className="text-xs text-gray-500 mt-1">Alleen Flux Dev.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Steps (10 – 50)
                    </label>
                    <input
                      type="number"
                      min="10"
                      max="50"
                      step="1"
                      value={bflSteps}
                      onChange={(e) => setBflSteps(e.target.value)}
                      disabled={bflModel !== 'flux-dev'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                    />
                    <p className="text-xs text-gray-500 mt-1">Alleen Flux Dev.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Safety tolerance (0 – 6)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="6"
                      step="1"
                      value={bflSafetyTolerance}
                      onChange={(e) => setBflSafetyTolerance(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">0 = strict, 6 = lenient.</p>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Raw photography mode</h4>
                    <p className="text-sm text-gray-500">
                      Alleen Ultra. Natuurlijke, ongepolijste fotolook.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bflUseRaw}
                      disabled={bflModel !== 'flux-pro-1.1-ultra'}
                      onChange={(e) => setBflUseRaw(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50"></div>
                  </label>
                </div>
              </div>

              <div className="pb-4 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Kosten & valuta</h3>
                <p className="text-xs text-gray-500 mb-4">
                  Verwachte richtprijzen — voor admin-weergave. De werkelijke BFL-kosten worden
                  per generatie via balance-delta uit de BFL API gelezen en in
                  <code className="text-xs bg-gray-100 px-1 py-0.5 rounded mx-1">ai_prompt_logs.cost_usd_actual</code>
                  vastgelegd.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Flux Dev (USD/image)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={bflCostPerImageUsd}
                      onChange={(e) => setBflCostPerImageUsd(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Flux Ultra (USD/image)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={bflCostPerImageUltraUsd}
                      onChange={(e) => setBflCostPerImageUltraUsd(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      USD → EUR koers
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={usdEurRate}
                      onChange={(e) => setUsdEurRate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Voor credit_log.</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSaveBFL}
                  disabled={saving === 'config'}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving === 'config' ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Opslaan...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      BFL-configuratie opslaan
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'leonardo' && (
        <div className="max-w-4xl">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Image className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Leonardo AI Configuratie</h2>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Leonardo API Key
                </label>
                <input
                  type="password"
                  value={leonardoApiKey}
                  onChange={(e) => setLeonardoApiKey(e.target.value)}
                  placeholder="Leonardo API key..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Deze key wordt gebruikt voor het genereren van afbeeldingen via Leonardo AI
                </p>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  Generatie Parameters
                  <a
                    href="https://docs.leonardo.ai/docs/commonly-used-api-values"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 transition-colors"
                    title="Leonardo AI API documentatie"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Model ID
                    </label>
                    <input
                      type="text"
                      value={leonardoModelId}
                      onChange={(e) => setLeonardoModelId(e.target.value)}
                      placeholder="Model ID"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Style UUID
                    </label>
                    <input
                      type="text"
                      value={leonardoStyleUuid}
                      onChange={(e) => setLeonardoStyleUuid(e.target.value)}
                      placeholder="Style UUID"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contrast (0-10)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={leonardoContrast}
                      onChange={(e) => setLeonardoContrast(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Aantal Afbeeldingen (1-8)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="8"
                      value={leonardoNumImages}
                      onChange={(e) => setLeonardoNumImages(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Breedte (pixels)
                    </label>
                    <input
                      type="number"
                      min="512"
                      max="2048"
                      step="64"
                      value={leonardoWidth}
                      onChange={(e) => setLeonardoWidth(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hoogte (pixels)
                    </label>
                    <input
                      type="number"
                      min="512"
                      max="2048"
                      step="64"
                      value={leonardoHeight}
                      onChange={(e) => setLeonardoHeight(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Ultra Quality Mode</h4>
                      <p className="text-sm text-gray-500">Hogere kwaliteit, maar hogere kosten</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={leonardoUltra}
                        onChange={(e) => setLeonardoUltra(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">Prompt Enhancement</h4>
                      <p className="text-sm text-gray-500">Laat Leonardo automatisch prompts verbeteren</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={leonardoEnhancePrompt}
                        onChange={(e) => setLeonardoEnhancePrompt(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={handleSaveLeonardo}
                  disabled={saving === 'config'}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving === 'config' ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Opslaan...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Opslaan
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'chatbot' && (
        <div className="space-y-6">
          {chatbotLoading ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Laden...</p>
            </div>
          ) : chatbotConfig ? (
            <>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-semibold text-gray-900">System Prompt</h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      System Prompt
                    </label>
                    <textarea
                      value={chatbotConfig.system_prompt}
                      onChange={(e) => setChatbotConfig({ ...chatbotConfig, system_prompt: e.target.value })}
                      rows={8}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                      placeholder="Systeem prompt voor de chatbot..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      De basis instructies die de chatbot volgt bij het beantwoorden van vragen
                    </p>
                  </div>

                  <div className="flex justify-end pt-4 border-t border-gray-200">
                    <button
                      onClick={handleSaveChatbotConfig}
                      disabled={saving === 'chatbot'}
                      className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving === 'chatbot' ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Opslaan...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Opslaan
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {chatbotAIConfig ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Cpu className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-900">AI Model Instellingen</h2>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Model
                      </label>
                      <select
                        value={chatbotAIConfig.model_name}
                        onChange={(e) => setChatbotAIConfig({ ...chatbotAIConfig, model_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="gpt-4o">GPT-4o (Aanbevolen)</option>
                        <option value="gpt-4o-mini">GPT-4o Mini (Goedkoper)</option>
                        <option value="gpt-4-turbo">GPT-4 Turbo</option>
                        <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Oudste)</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Temperature
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="2"
                          step="0.01"
                          value={chatbotAIConfig.temperature}
                          onChange={(e) => setChatbotAIConfig({ ...chatbotAIConfig, temperature: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">0 = Consistent, 2 = Creatief</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Max Tokens
                        </label>
                        <input
                          type="number"
                          min="100"
                          max="4000"
                          step="100"
                          value={chatbotAIConfig.max_tokens}
                          onChange={(e) => setChatbotAIConfig({ ...chatbotAIConfig, max_tokens: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Top P
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="1"
                          step="0.01"
                          value={chatbotAIConfig.top_p}
                          onChange={(e) => setChatbotAIConfig({ ...chatbotAIConfig, top_p: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <p className="text-xs text-gray-500 mt-1">0 = Gefocust, 1 = Divers</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Frequency Penalty
                        </label>
                        <input
                          type="number"
                          min="-2"
                          max="2"
                          step="0.1"
                          value={chatbotAIConfig.frequency_penalty}
                          onChange={(e) => setChatbotAIConfig({ ...chatbotAIConfig, frequency_penalty: parseFloat(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Presence Penalty
                      </label>
                      <input
                        type="number"
                        min="-2"
                        max="2"
                        step="0.1"
                        value={chatbotAIConfig.presence_penalty}
                        onChange={(e) => setChatbotAIConfig({ ...chatbotAIConfig, presence_penalty: parseFloat(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notities
                      </label>
                      <textarea
                        value={chatbotAIConfig.notes || ''}
                        onChange={(e) => setChatbotAIConfig({ ...chatbotAIConfig, notes: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Optionele notities..."
                      />
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <div className={`w-2 h-2 rounded-full ${chatbotAIConfig.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <span className="text-gray-700">
                        {chatbotAIConfig.is_active ? 'Actief' : 'Inactief'}
                      </span>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-gray-200">
                      <button
                        onClick={handleSaveChatbotAIConfig}
                        disabled={saving === 'config'}
                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving === 'config' ? (
                          <>
                            <Loader className="w-4 h-4 animate-spin" />
                            Opslaan...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            Opslaan
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <p className="text-amber-800">
                      Geen chatbot_query configuratie gevonden. Controleer de database.
                    </p>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <p className="text-red-800">
                  Geen chatbot configuratie gevonden. Controleer of de database migratie correct is uitgevoerd.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      <SlideOver
        isOpen={isPanelOpen}
        onClose={closePanel}
        title={editingItem
          ? `${promptTypeLabels[editingItem.prompt_type] || editingItem.prompt_type} - v${editingItem.prompt.version}`
          : 'AI Configuratie Bewerken'
        }
        width="wide"
      >
        <div className="p-6 space-y-6">
          {panelMessage && (
            <div
              className={`p-4 rounded-lg flex items-center gap-3 ${
                panelMessage.type === 'success'
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
            >
              {panelMessage.type === 'success' ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <span>{panelMessage.text}</span>
            </div>
          )}

          {editingItem && editingConfig && (
            <>
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h3 className="font-semibold text-amber-900 mb-2">Wanneer wordt dit gebruikt?</h3>
                <p className="text-amber-800 text-sm leading-relaxed">
                  {getPromptDescription(editingItem.prompt_type)}
                </p>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
                  <Cpu className="w-5 h-5 text-blue-600" />
                  AI Model Instellingen
                </h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Model
                    </label>
                    <select
                      value={editingConfig.model_name}
                      onChange={(e) => handleConfigChange('model_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="gpt-4o">GPT-4o (Aanbevolen)</option>
                      <option value="gpt-4o-mini">GPT-4o Mini (Goedkoper)</option>
                      <option value="gpt-4-turbo">GPT-4 Turbo</option>
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Oudste)</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Temperature
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="2"
                        step="0.01"
                        value={editingConfig.temperature}
                        onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">0 = Consistent, 2 = Creatief</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Tokens
                      </label>
                      <input
                        type="number"
                        min="100"
                        max="4000"
                        step="100"
                        value={editingConfig.max_tokens}
                        onChange={(e) => handleConfigChange('max_tokens', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Top P
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        value={editingConfig.top_p}
                        onChange={(e) => handleConfigChange('top_p', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">0 = Gefocust, 1 = Divers</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Frequency Penalty
                      </label>
                      <input
                        type="number"
                        min="-2"
                        max="2"
                        step="0.1"
                        value={editingConfig.frequency_penalty}
                        onChange={(e) => handleConfigChange('frequency_penalty', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Presence Penalty
                      </label>
                      <input
                        type="number"
                        min="-2"
                        max="2"
                        step="0.1"
                        value={editingConfig.presence_penalty}
                        onChange={(e) => handleConfigChange('presence_penalty', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notities
                    </label>
                    <textarea
                      value={editingConfig.notes || ''}
                      onChange={(e) => handleConfigChange('notes', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Optionele notities..."
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 mb-4">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  Prompt Template
                </h3>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Beschikbare Placeholders:</h4>
                  <div className="flex flex-wrap gap-2">
                    {getPromptPlaceholders(editingItem.prompt_type).map((placeholder) => (
                      <code
                        key={placeholder}
                        className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-mono"
                      >
                        {placeholder}
                      </code>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Prompt Content
                  </label>
                  <textarea
                    value={editingPromptContent}
                    onChange={(e) => setEditingPromptContent(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent font-mono text-sm resize-none"
                    rows={15}
                    placeholder="Voer de prompt content in..."
                  />
                  <p className="text-xs text-gray-500">
                    {editingPromptContent.length} karakters
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={closePanel}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving === 'both'}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving === 'both' ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Opslaan...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Opslaan
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </SlideOver>
    </AdminLayout>
  );
}
